import { Dot, Shape } from '@/features/map/types/shape'
import React, { useMemo } from 'react'
import { Circle, Layer, Line, Stage, Text } from 'react-konva'

type Props = {
  departmentShape?: Shape
  gatewayDots?: Dot[]
  deviceDots?: Dot[]

  width?: number
  height?: number
}

const PADDING = 40

function getBounds(points: [number, number][]) {
  const xs = points.map(p => p[0])
  const ys = points.map(p => p[1])

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  }
}

function getTransform(points: [number, number][], width: number, height: number) {
  const bounds = getBounds(points)

  const polygonWidth = bounds.maxX - bounds.minX
  const polygonHeight = bounds.maxY - bounds.minY

  const scaleX = (width - PADDING * 2) / polygonWidth
  const scaleY = (height - PADDING * 2) / polygonHeight

  const scale = Math.min(scaleX, scaleY)

  const scaledWidth = polygonWidth * scale
  const scaledHeight = polygonHeight * scale

  const offsetX = (width - scaledWidth) / 2
  const offsetY = (height - scaledHeight) / 2

  return {
    bounds,
    scale,
    offsetX,
    offsetY,
  }
}

function normalizePolygon(points: [number, number][], width: number, height: number) {
  const { bounds, scale, offsetX, offsetY } = getTransform(points, width, height)

  return points.map(([x, y]) => [(x - bounds.minX) * scale + offsetX, (y - bounds.minY) * scale + offsetY]) as [
    number,
    number,
  ][]
}

function normalizePoint(
  point: [number, number],
  polygon: [number, number][],
  width: number,
  height: number,
): [number, number] {
  const { bounds, scale, offsetX, offsetY } = getTransform(polygon, width, height)

  return [(point[0] - bounds.minX) * scale + offsetX, (point[1] - bounds.minY) * scale + offsetY]
}

function toFlatPoints(points: [number, number][]) {
  return points.flatMap(([x, y]) => [x, y])
}

export function DepartmentPreview({
  departmentShape,
  gatewayDots = [],
  deviceDots = [],
  width = 600,
  height = 400,
}: Props) {
  if (!departmentShape || !departmentShape.points || departmentShape.points.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-md border bg-muted/20 h-[400px] w-full">
        <span className="text-xs text-muted-foreground w-full text-center">No department area</span>
      </div>
    )
  }

  const normalizedPolygon = normalizePolygon(departmentShape.points, width, height)

  const gatewayCounts = useMemo(
    () =>
      deviceDots.reduce(
        (acc, device) => {
          if (!device.gatewayId) return acc
          acc[device.gatewayId] = (acc[device.gatewayId] ?? 0) + 1
          return acc
        },
        {} as Record<string, number>,
      ),
    [deviceDots],
  )

  return (
    <div className="rounded-md border bg-muted/10 overflow-auto">
      <Stage width={width} height={height}>
        <Layer>
          {/* Department Polygon */}
          <Line
            points={toFlatPoints(normalizedPolygon)}
            closed
            fill="rgba(59,130,246,0.25)"
            stroke="#2563eb"
            strokeWidth={2}
          />

          {/* Department Name */}
          <Text text={departmentShape.label} x={16} y={16} fontSize={16} fontStyle="bold" fill="#111827" />

          {/* Gateways */}
          {gatewayDots.map(gateway => {
            const [x, y] = normalizePoint(gateway.point, departmentShape.points!, width, height)

            const isBLE = gateway.gatewayType === 'BLE'

            return (
              <React.Fragment key={gateway.id}>
                <Circle x={x} y={y} radius={7} fill={isBLE ? '#2563eb' : '#dc2626'} stroke="white" strokeWidth={2} />

                <Text text={gateway.label} x={x + 10} y={y - 8} fontSize={11} fill="#111827" />
              </React.Fragment>
            )
          })}

          {/* Devices */}
          {deviceDots.map(device => {
            const [x, y] = normalizePoint(device.point, departmentShape.points!, width, height)
            const count = device.gatewayId ? (gatewayCounts[device.gatewayId] ?? 1) : 1
            const opacity = count >= 3 ? 1 : count === 2 ? 0.8 : 0.6
            return (
              <React.Fragment key={device.id}>
                <Circle x={x} y={y} radius={6} fill="#22c55e" stroke="white" strokeWidth={2} opacity={opacity} />

                <Text text={device.label} x={x + 10} y={y - 8} fontSize={11} fill="#111827" />
              </React.Fragment>
            )
          })}
        </Layer>
      </Stage>
    </div>
  )
}
